import { useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import type { TOrganizationProjectDimension } from "@/zapi-frontend/shared/types/projects";
import { ALL_SYSTEM_COLUMN_IDS, EMPTY_VALUE_TOKEN, HAS_VALUE_TOKEN, MODIFIED_BY_FILTER_KEY, OWNER_FILTER_KEY, STATUS_FILTER_KEY } from "../const/const";
import {
  clampFiltersToDimensionIds,
  clampPrefsToDimensionIds,
  dimensionIdsFromList
} from "./clampPortfolioToDimensions";
import {
  filtersFromSaved,
  savedFromFilters,
  type TActiveFilters,
  type TPortfolioPrefs,
  type TSystemColumn
} from "./portfolioStorage";
import { loadPortfolioPrefs, savePortfolioPrefs } from "./portfolioPrefsStorage";

type TParams = {
  organizationId: number;
  dimensions: TOrganizationProjectDimension[];
  dimensionsLoaded: boolean;
};

const RESERVED_KEYS = new Set(["view", "sort", "dir", "hiddenColumns", "hiddenDimensions", "search"]);

const DEFAULTS: TPortfolioPrefs = {
  viewMode: "table",
  hiddenDimensionIds: [],
  hiddenSystemColumns: [],
  sortFieldKey: "lastModified",
  sortDir: "desc",
  savedFilters: {}
};

function sortKeyToUrl(key: string): string {
  if (key.startsWith("dim:")) return `dim${key.slice(4)}`;
  return key;
}

function sortKeyFromUrl(urlKey: string): string {
  const dimMatch = /^dim(\d+)$/.exec(urlKey);
  if (dimMatch) return `dim:${dimMatch[1]}`;
  return urlKey;
}

function filterKeyToUrl(internalKey: string): string {
  if (internalKey === STATUS_FILTER_KEY) return "status";
  if (internalKey === OWNER_FILTER_KEY) return "owner";
  if (internalKey === MODIFIED_BY_FILTER_KEY) return "modifiedBy";
  return `dim${internalKey}`;
}

export function filterKeyFromUrl(urlKey: string): string | null {
  if (urlKey === "status") return STATUS_FILTER_KEY;
  if (urlKey === "owner") return OWNER_FILTER_KEY;
  if (urlKey === "modifiedBy") return MODIFIED_BY_FILTER_KEY;
  const dimMatch = /^dim(\d+)$/.exec(urlKey);
  if (dimMatch) return dimMatch[1];
  return null;
}

function valueToUrl(value: string): string {
  if (value === HAS_VALUE_TOKEN) return "hasValue";
  if (value === EMPTY_VALUE_TOKEN) return "empty";
  return value;
}

function valueFromUrl(value: string): string {
  if (value === "hasValue") return HAS_VALUE_TOKEN;
  if (value === "empty") return EMPTY_VALUE_TOKEN;
  return value;
}

function readPrefsFromParams(params: URLSearchParams): TPortfolioPrefs {
  const view = params.get("view");
  const sortRaw = params.get("sort");
  const dir = params.get("dir") === "asc" ? "asc" : "desc";
  const hiddenSystemColumns = (params.get("hiddenColumns") ?? "")
    .split(",")
    .filter((c): c is TSystemColumn => (ALL_SYSTEM_COLUMN_IDS as string[]).includes(c));
  const hiddenDimensionIds = (params.get("hiddenDimensions") ?? "").split(",").filter(Boolean);

  const savedFilters: Record<string, string[]> = {};
  for (const [k, v] of params.entries()) {
    if (RESERVED_KEYS.has(k)) continue;
    const internalKey = filterKeyFromUrl(k);
    if (internalKey === null) continue;
    const arr = v.split(",").filter(Boolean).map(valueFromUrl);
    if (arr.length) savedFilters[internalKey] = arr;
  }

  return {
    viewMode: view === "cards" ? "cards" : "table",
    hiddenSystemColumns,
    hiddenDimensionIds,
    sortFieldKey: sortRaw ? sortKeyFromUrl(sortRaw) : DEFAULTS.sortFieldKey,
    sortDir: dir,
    savedFilters
  };
}

function paramsHavePortfolioState(params: URLSearchParams): boolean {
  for (const k of params.keys()) {
    if (k === "view" || k === "sort" || k === "dir" || k === "hiddenColumns" || k === "hiddenDimensions") return true;
    if (filterKeyFromUrl(k) !== null) return true;
  }
  return false;
}

function writePrefsToParams(prev: URLSearchParams, prefs: TPortfolioPrefs): URLSearchParams {
  const next = new URLSearchParams(prev);

  if (prefs.viewMode === "cards") next.set("view", "cards");
  else next.delete("view");

  if (prefs.sortFieldKey !== DEFAULTS.sortFieldKey) next.set("sort", sortKeyToUrl(prefs.sortFieldKey));
  else next.delete("sort");

  if (prefs.sortDir !== DEFAULTS.sortDir) next.set("dir", prefs.sortDir);
  else next.delete("dir");

  if (prefs.hiddenSystemColumns.length) next.set("hiddenColumns", prefs.hiddenSystemColumns.join(","));
  else next.delete("hiddenColumns");

  if (prefs.hiddenDimensionIds.length) next.set("hiddenDimensions", prefs.hiddenDimensionIds.join(","));
  else next.delete("hiddenDimensions");

  for (const k of [...next.keys()]) {
    if (RESERVED_KEYS.has(k)) continue;
    if (filterKeyFromUrl(k) !== null) next.delete(k);
  }
  for (const [internalKey, arr] of Object.entries(prefs.savedFilters)) {
    if (!arr.length) continue;
    next.set(filterKeyToUrl(internalKey), arr.map(valueToUrl).join(","));
  }

  return next;
}

export function usePortfolioPrefs({ organizationId, dimensions, dimensionsLoaded }: TParams) {
  const [searchParams, setSearchParams] = useSearchParams();

  const prefs = useMemo(() => readPrefsFromParams(searchParams), [searchParams]);
  const filters: TActiveFilters = useMemo(() => filtersFromSaved(prefs.savedFilters), [prefs.savedFilters]);

  const hydratedOrgRef = useRef<number | null>(null);
  useEffect(() => {
    if (!organizationId) return;
    if (hydratedOrgRef.current === organizationId) return;
    const isFirstRun = hydratedOrgRef.current === null;
    hydratedOrgRef.current = organizationId;

    if (isFirstRun && paramsHavePortfolioState(searchParams)) {
      savePortfolioPrefs(organizationId, readPrefsFromParams(searchParams));
      return;
    }

    const stored = loadPortfolioPrefs(organizationId);
    setSearchParams((cur) => writePrefsToParams(cur, stored ?? DEFAULTS), { replace: true });
  }, [organizationId, searchParams, setSearchParams]);

  useEffect(() => {
    if (!dimensionsLoaded) return;
    const dimIds = dimensionIdsFromList(dimensions);
    const clampedPrefs = clampPrefsToDimensionIds(prefs, dimIds);
    const clampedFilters = clampFiltersToDimensionIds(filters, dimIds);
    if (!clampedPrefs && !clampedFilters) return;
    const nextPrefs: TPortfolioPrefs = {
      ...(clampedPrefs ?? prefs),
      savedFilters: clampedFilters ? savedFromFilters(clampedFilters) : (clampedPrefs?.savedFilters ?? prefs.savedFilters)
    };
    setSearchParams((cur) => writePrefsToParams(cur, nextPrefs), { replace: true });
  }, [dimensions, dimensionsLoaded, prefs, filters, setSearchParams]);

  const updatePrefs = (updater: (current: TPortfolioPrefs) => TPortfolioPrefs) => {
    const next = updater(prefs);
    savePortfolioPrefs(organizationId, next);
    setSearchParams((cur) => writePrefsToParams(cur, next), { replace: true });
  };

  const persist = (patch: Partial<TPortfolioPrefs>) => {
    updatePrefs((cur) => ({ ...cur, ...patch }));
  };

  const toggleSystemColumn = (col: TSystemColumn, checked: boolean) => {
    updatePrefs((cur) => {
      const set = new Set(cur.hiddenSystemColumns);
      if (checked) set.delete(col);
      else set.add(col);
      return { ...cur, hiddenSystemColumns: [...set] as TSystemColumn[] };
    });
  };

  const toggleDimensionVisibility = (id: string, checked: boolean) => {
    updatePrefs((cur) => {
      const hidden = new Set(cur.hiddenDimensionIds);
      if (checked) hidden.delete(id);
      else hidden.add(id);
      return { ...cur, hiddenDimensionIds: [...hidden] };
    });
  };

  const hideAllColumns = () => {
    const allDimIds = dimensions.map((d) => String(d.id));
    updatePrefs((cur) => ({
      ...cur,
      hiddenSystemColumns: ALL_SYSTEM_COLUMN_IDS,
      hiddenDimensionIds: allDimIds
    }));
  };

  const showAllColumns = () => {
    updatePrefs((cur) => ({ ...cur, hiddenSystemColumns: [], hiddenDimensionIds: [] }));
  };

  const toggleFilterValue = (dimId: string, value: string, checked: boolean) => {
    updatePrefs((cur) => {
      const next = { ...cur.savedFilters };
      const set = new Set(next[dimId] ?? []);
      if (checked) set.add(value);
      else set.delete(value);
      if (set.size === 0) delete next[dimId];
      else next[dimId] = [...set];
      return { ...cur, savedFilters: next };
    });
  };

  const clearAllFilters = () => {
    updatePrefs((cur) => ({ ...cur, savedFilters: {} }));
  };

  const resetAll = () => {
    updatePrefs((cur) => ({
      ...cur,
      savedFilters: {},
      sortFieldKey: DEFAULTS.sortFieldKey,
      sortDir: DEFAULTS.sortDir
    }));
  };

  const resetSort = () => {
    updatePrefs((cur) => ({
      ...cur,
      sortFieldKey: DEFAULTS.sortFieldKey,
      sortDir: DEFAULTS.sortDir
    }));
  };

  const canReset =
    Object.keys(prefs.savedFilters).length > 0 ||
    prefs.sortFieldKey !== DEFAULTS.sortFieldKey ||
    prefs.sortDir !== DEFAULTS.sortDir;

  return {
    prefs,
    filters,
    persist,
    toggleSystemColumn,
    toggleDimensionVisibility,
    hideAllColumns,
    showAllColumns,
    toggleFilterValue,
    clearAllFilters,
    resetAll,
    resetSort,
    canReset
  };
}
